import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

/**
 * CameraController - Manages interactive camera controls for the 3D scene
 * Provides orbit, zoom, and pan controls with mobile touch support
 */
export class CameraController {
    constructor(camera, domElement) {
        this.camera = camera;
        this.domElement = domElement;
        this.controls = null;
        this.isEnabled = true;

        // Control settings
        this.settings = {
            enableDamping: true,
            dampingFactor: 0.05,
            enableZoom: true,
            enableRotate: true,
            enablePan: true,
            autoRotate: false,
            autoRotateSpeed: 2.0,
            minDistance: 10,
            maxDistance: 50000, // permite alejarse para ver la heliosfera completa (cola ~21k u)
            minPolarAngle: 0,
            maxPolarAngle: Math.PI,
            minAzimuthAngle: -Infinity,
            maxAzimuthAngle: Infinity,
        };

        // Camera tracking system
        this.tracking = {
            enabled: false,
            target: null,
            targetObject: null,
            distance: 100,
            smoothTransition: true,
            transitionSpeed: 2.0,
            isTransitioning: false,
            transitionStartTime: 0,
            transitionDuration: 1000, // ms
            startPosition: new THREE.Vector3(),
            startTarget: new THREE.Vector3(),
            endPosition: new THREE.Vector3(),
            endTarget: new THREE.Vector3(),
            lastTargetPosition: new THREE.Vector3(), // posición del objeto el frame anterior (follow rígido)
        };

        // POV (Point of View) mode system
        this.povMode = {
            enabled: false,
            planetObject: null,
            sunObject: null,
            originalCameraPosition: new THREE.Vector3(),
            originalTarget: new THREE.Vector3(),
        };

        // Touch settings for mobile devices
        this.touchSettings = {
            enableTouchRotate: true,
            enableTouchZoom: true,
            enableTouchPan: true,
            touchRotateSpeed: 1.0,
            touchZoomSpeed: 1.0,
            touchPanSpeed: 1.0,
        };

        this.initializeControls();
    }

    /**
     * Set references to solar system objects for POV mode
     * @param {THREE.Object3D} sun - Sun object
     * @param {Map} planets - Map of planet objects
     */
    setSolarSystemObjects(sun, planets) {
        this.solarSystemObjects = {
            sun: sun,
            planets: planets,
        };
    }

    /**
     * Initialize OrbitControls with configured settings
     */
    initializeControls() {
        try {
            // Create OrbitControls instance
            this.controls = new OrbitControls(this.camera, this.domElement);

            // Apply basic settings
            this.applyControlSettings();

            // Setup mobile touch controls
            this.setupTouchControls();

            // Setup event listeners
            this.setupEventListeners();

            console.log('Camera controls initialized successfully');
        } catch (error) {
            console.error('Error initializing camera controls:', error);
            throw new Error(`Failed to initialize camera controls: ${error.message}`);
        }
    }

    /**
     * Apply control settings to OrbitControls
     */
    applyControlSettings() {
        if (!this.controls) return;

        // Damping settings for smooth movement
        this.controls.enableDamping = this.settings.enableDamping;
        this.controls.dampingFactor = this.settings.dampingFactor;

        // Enable/disable controls
        this.controls.enableZoom = this.settings.enableZoom;
        this.controls.enableRotate = this.settings.enableRotate;
        this.controls.enablePan = this.settings.enablePan;

        // Auto rotation
        this.controls.autoRotate = this.settings.autoRotate;
        this.controls.autoRotateSpeed = this.settings.autoRotateSpeed;

        // Distance limits (zoom limits)
        this.controls.minDistance = this.settings.minDistance;
        this.controls.maxDistance = this.settings.maxDistance;

        // Angle limits
        this.controls.minPolarAngle = this.settings.minPolarAngle;
        this.controls.maxPolarAngle = this.settings.maxPolarAngle;
        this.controls.minAzimuthAngle = this.settings.minAzimuthAngle;
        this.controls.maxAzimuthAngle = this.settings.maxAzimuthAngle;

        // Mouse/keyboard settings
        this.controls.mouseButtons = {
            LEFT: THREE.MOUSE.ROTATE,
            MIDDLE: THREE.MOUSE.DOLLY,
            RIGHT: THREE.MOUSE.PAN,
        };

        this.controls.keys = {
            LEFT: 'ArrowLeft',
            UP: 'ArrowUp',
            RIGHT: 'ArrowRight',
            BOTTOM: 'ArrowDown',
        };
    }

    /**
     * Setup touch controls for mobile devices
     */
    setupTouchControls() {
        if (!this.controls) return;

        // Enable touch controls
        this.controls.enableTouchRotate = this.touchSettings.enableTouchRotate;
        this.controls.enableTouchZoom = this.touchSettings.enableTouchZoom;
        this.controls.enableTouchPan = this.touchSettings.enableTouchPan;

        // Touch speed settings
        this.controls.rotateSpeed = this.touchSettings.touchRotateSpeed;
        this.controls.zoomSpeed = this.touchSettings.touchZoomSpeed;
        this.controls.panSpeed = this.touchSettings.touchPanSpeed;

        // Touch button mapping
        this.controls.touches = {
            ONE: THREE.TOUCH.ROTATE,
            TWO: THREE.TOUCH.DOLLY_PAN,
        };
    }

    /**
     * Setup event listeners for control events
     */
    setupEventListeners() {
        if (!this.controls) return;

        // Listen for control changes
        this.controls.addEventListener('change', () => {
            // This event fires when the camera position changes
            this.onCameraChange();
        });

        // Listen for start/end of interaction
        this.controls.addEventListener('start', () => {
            this.onInteractionStart();
        });

        this.controls.addEventListener('end', () => {
            this.onInteractionEnd();
        });
    }

    /**
     * Handle camera position changes
     */
    onCameraChange() {
        // Override in subclass or set callback if needed
        // This can be used to update UI elements based on camera position
    }

    /**
     * Handle start of user interaction
     */
    onInteractionStart() {
        // Override in subclass or set callback if needed
        // This can be used to pause auto-rotation or show UI hints
    }

    /**
     * Handle end of user interaction
     */
    onInteractionEnd() {
        // Override in subclass or set callback if needed
        // This can be used to resume auto-rotation or hide UI hints
    }

    /**
     * Enable camera controls
     */
    enableControls() {
        this.isEnabled = true;
        if (this.controls) {
            this.controls.enabled = true;
        }
    }

    /**
     * Disable camera controls
     */
    disableControls() {
        this.isEnabled = false;
        if (this.controls) {
            this.controls.enabled = false;
        }
    }

    /**
     * Focus camera on a specific object or position
     * @param {THREE.Object3D|THREE.Vector3} target - Object or position to focus on
     * @param {number} distance - Optional distance from target
     */
    focusOnObject(target, distance = null) {
        if (!this.controls) return;

        let targetPosition;

        if (target instanceof THREE.Object3D) {
            targetPosition = target.position.clone();
        } else if (target instanceof THREE.Vector3) {
            targetPosition = target.clone();
        } else {
            console.warn('Invalid target for camera focus');
            return;
        }

        // Set the controls target
        this.controls.target.copy(targetPosition);

        // Optionally set camera distance
        if (distance !== null) {
            const direction = this.camera.position.clone().sub(targetPosition).normalize();
            this.camera.position.copy(targetPosition).add(direction.multiplyScalar(distance));
        }

        this.controls.update();
    }

    /**
     * Set zoom limits
     * @param {number} min - Minimum zoom distance
     * @param {number} max - Maximum zoom distance
     */
    setZoomLimits(min, max) {
        this.settings.minDistance = min;
        this.settings.maxDistance = max;

        if (this.controls) {
            this.controls.minDistance = min;
            this.controls.maxDistance = max;
        }
    }

    /**
     * Set rotation limits
     * @param {number} minPolar - Minimum polar angle (vertical rotation)
     * @param {number} maxPolar - Maximum polar angle (vertical rotation)
     * @param {number} minAzimuth - Minimum azimuth angle (horizontal rotation)
     * @param {number} maxAzimuth - Maximum azimuth angle (horizontal rotation)
     */
    setRotationLimits(
        minPolar = 0,
        maxPolar = Math.PI,
        minAzimuth = -Infinity,
        maxAzimuth = Infinity
    ) {
        this.settings.minPolarAngle = minPolar;
        this.settings.maxPolarAngle = maxPolar;
        this.settings.minAzimuthAngle = minAzimuth;
        this.settings.maxAzimuthAngle = maxAzimuth;

        if (this.controls) {
            this.controls.minPolarAngle = minPolar;
            this.controls.maxPolarAngle = maxPolar;
            this.controls.minAzimuthAngle = minAzimuth;
            this.controls.maxAzimuthAngle = maxAzimuth;
        }
    }

    /**
     * Enable or disable auto rotation
     * @param {boolean} enabled - Whether auto rotation should be enabled
     * @param {number} speed - Auto rotation speed
     */
    setAutoRotate(enabled, speed = 2.0) {
        this.settings.autoRotate = enabled;
        this.settings.autoRotateSpeed = speed;

        if (this.controls) {
            this.controls.autoRotate = enabled;
            this.controls.autoRotateSpeed = speed;
        }
    }

    /**
     * Set damping settings for smooth movement
     * @param {boolean} enabled - Whether damping should be enabled
     * @param {number} factor - Damping factor (0-1)
     */
    setDamping(enabled, factor = 0.05) {
        this.settings.enableDamping = enabled;
        this.settings.dampingFactor = factor;

        if (this.controls) {
            this.controls.enableDamping = enabled;
            this.controls.dampingFactor = factor;
        }
    }

    /**
     * Reset camera to default position
     * @param {THREE.Vector3} position - Default camera position
     * @param {THREE.Vector3} target - Default target position
     */
    reset(position = new THREE.Vector3(0, 50, 100), target = new THREE.Vector3(0, 0, 0)) {
        if (!this.controls) return;

        this.camera.position.copy(position);
        this.controls.target.copy(target);
        this.controls.update();
    }

    /**
     * Get current camera state
     * @returns {Object} Camera state information
     */
    getCameraState() {
        if (!this.controls) return null;

        return {
            position: this.camera.position.clone(),
            target: this.controls.target.clone(),
            distance: this.camera.position.distanceTo(this.controls.target),
            enabled: this.isEnabled,
            autoRotate: this.controls.autoRotate,
        };
    }

    /**
     * Check if device supports touch
     * @returns {boolean} True if touch is supported
     */
    isTouchDevice() {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }

    /**
     * Optimize settings for mobile devices
     */
    optimizeForMobile() {
        if (this.isTouchDevice()) {
            // Adjust settings for better mobile performance
            this.setDamping(true, 0.1); // Slightly higher damping for touch
            this.touchSettings.touchRotateSpeed = 0.8; // Slower rotation for better control
            this.touchSettings.touchZoomSpeed = 0.8; // Slower zoom for better control

            if (this.controls) {
                this.controls.rotateSpeed = this.touchSettings.touchRotateSpeed;
                this.controls.zoomSpeed = this.touchSettings.touchZoomSpeed;
            }

            console.log('Camera controls optimized for mobile device');
        }
    }

    /**
     * Dispose of controls and cleanup
     */
    dispose() {
        if (this.controls) {
            this.controls.dispose();
            this.controls = null;
        }
    }

    /**
     * Get the OrbitControls instance
     * @returns {OrbitControls} The controls instance
     */
    getControls() {
        return this.controls;
    }

    /**
     * Enable automatic tracking of an object
     * @param {THREE.Object3D|THREE.Vector3} target - Object or position to track
     * @param {number} distance - Distance to maintain from target
     * @param {boolean} smoothTransition - Whether to use smooth transition
     */
    startTracking(target, distance = 100, smoothTransition = true) {
        if (!target) {
            console.warn('Cannot start tracking: invalid target');
            return;
        }

        const targetPosition = this.getTargetPosition(target);
        if (!targetPosition) {
            console.warn('Cannot start tracking: unable to get target position');
            return;
        }

        // Store tracking configuration
        this.tracking.targetObject = target;
        this.tracking.distance = distance;
        this.tracking.smoothTransition = smoothTransition;
        // Referencia para el seguimiento rígido por delta (se re-sincroniza al acabar la transición)
        this.tracking.lastTargetPosition.copy(targetPosition);

        if (smoothTransition && this.controls) {
            // Setup smooth transition
            this.setupSmoothTransition(targetPosition, distance);
        } else {
            // Immediate tracking
            this.setImmediateTracking(targetPosition, distance);
        }

        this.tracking.enabled = true;
        console.log('Camera tracking started for target:', target.name || 'unnamed object');
    }

    /**
     * Stop automatic tracking
     * @param {boolean} smoothTransition - Whether to use smooth transition when stopping
     */
    stopTracking(smoothTransition = true) {
        if (!this.tracking.enabled) return;

        if (smoothTransition && this.tracking.isTransitioning) {
            // Allow current transition to complete before stopping
            this.tracking.isTransitioning = false;
        }

        this.tracking.enabled = false;
        this.tracking.targetObject = null;
        this.tracking.isTransitioning = false;

        console.log('Camera tracking stopped');
    }

    /**
     * Check if camera is currently tracking an object
     * @returns {boolean} True if tracking is active
     */
    isTracking() {
        return this.tracking.enabled;
    }

    /**
     * Get the currently tracked object
     * @returns {THREE.Object3D|THREE.Vector3|null} Currently tracked target
     */
    getTrackedObject() {
        return this.tracking.targetObject;
    }

    /**
     * Set tracking distance
     * @param {number} distance - New tracking distance
     */
    setTrackingDistance(distance) {
        this.tracking.distance = distance;

        // Si estamos siguiendo, reescalar el offset actual a la nueva distancia (mantiene el ángulo)
        if (this.tracking.enabled && !this.tracking.isTransitioning && this.controls) {
            const offset = this.camera.position.clone().sub(this.controls.target);
            const len = offset.length();
            if (len > 1e-6) {
                offset.multiplyScalar(distance / len);
                this.camera.position.copy(this.controls.target).add(offset);
                this.controls.update();
            }
        }
    }

    /**
     * Get current tracking distance
     * @returns {number} Current tracking distance
     */
    getTrackingDistance() {
        return this.tracking.distance;
    }

    /**
     * Update tracking system (should be called in animation loop)
     */
    updateTracking() {
        if (!this.tracking.enabled || !this.tracking.targetObject) return;

        const currentTime = performance.now();

        // Handle smooth transition
        if (this.tracking.isTransitioning) {
            this.updateSmoothTransition(currentTime);
            return;
        }

        // Seguimiento RÍGIDO: trasladar cámara y target por el MISMO desplazamiento que el
        // objeto desde el último frame. Así la cámara conserva su posición RELATIVA al planeta
        // (si la dejas delante, se queda delante a la misma distancia) mientras este se mueve,
        // y OrbitControls sigue permitiendo reorbitar/zoom (que redefine ese offset relativo).
        const targetPosition = this.getTargetPosition(this.tracking.targetObject);
        if (targetPosition && this.controls) {
            const delta = targetPosition.clone().sub(this.tracking.lastTargetPosition);
            this.camera.position.add(delta);
            this.controls.target.add(delta);
            this.tracking.lastTargetPosition.copy(targetPosition);
            this.controls.update();
        }
    }

    /**
     * Get position from target object or vector
     * @param {THREE.Object3D|THREE.Vector3} target - Target to get position from
     * @returns {THREE.Vector3|null} Target position
     */
    getTargetPosition(target) {
        if (target instanceof THREE.Object3D) {
            return target.position.clone();
        } else if (target instanceof THREE.Vector3) {
            return target.clone();
        }
        return null;
    }

    /**
     * Setup smooth transition to new tracking target
     * @param {THREE.Vector3} targetPosition - Position to transition to
     * @param {number} distance - Distance to maintain
     */
    setupSmoothTransition(targetPosition, distance) {
        if (!this.controls) return;

        this.tracking.isTransitioning = true;
        this.tracking.transitionStartTime = performance.now();

        // Store start positions
        this.tracking.startPosition.copy(this.camera.position);
        this.tracking.startTarget.copy(this.controls.target);

        // Calculate end positions
        this.tracking.endTarget.copy(targetPosition);

        // Calculate optimal camera position for the new target
        const direction = this.camera.position.clone().sub(this.controls.target).normalize();
        this.tracking.endPosition.copy(targetPosition).add(direction.multiplyScalar(distance));
    }

    /**
     * Set immediate tracking without transition
     * @param {THREE.Vector3} targetPosition - Position to track
     * @param {number} distance - Distance to maintain
     */
    setImmediateTracking(targetPosition, distance) {
        if (!this.controls) return;

        // Set controls target
        this.controls.target.copy(targetPosition);

        // Position camera at appropriate distance
        const direction = this.camera.position.clone().sub(targetPosition).normalize();
        this.camera.position.copy(targetPosition).add(direction.multiplyScalar(distance));

        this.controls.update();
    }

    /**
     * Update smooth transition animation
     * @param {number} currentTime - Current timestamp
     */
    updateSmoothTransition(currentTime) {
        const elapsed = currentTime - this.tracking.transitionStartTime;
        const progress = Math.min(elapsed / this.tracking.transitionDuration, 1);

        // Use easing function for smooth transition
        const easedProgress = this.easeInOutCubic(progress);

        // Interpolate camera position
        this.camera.position.lerpVectors(
            this.tracking.startPosition,
            this.tracking.endPosition,
            easedProgress
        );

        // Interpolate controls target
        this.controls.target.lerpVectors(
            this.tracking.startTarget,
            this.tracking.endTarget,
            easedProgress
        );

        this.controls.update();

        // Check if transition is complete
        if (progress >= 1) {
            this.tracking.isTransitioning = false;
            // Re-sincronizar la referencia del seguimiento rígido: el objeto se movió durante la
            // transición, así evitamos un salto en el primer frame de follow.
            const tp = this.getTargetPosition(this.tracking.targetObject);
            if (tp) this.tracking.lastTargetPosition.copy(tp);
            console.log('Camera tracking transition completed');
        }
    }

    /**
     * Check if user is currently interacting with controls
     * @returns {boolean} True if user is interacting
     */
    isUserInteracting() {
        // This is a simple check - in a more complex system you might track
        // mouse/touch events more precisely
        return false; // For now, always allow tracking adjustments
    }

    /**
     * Easing function for smooth transitions
     * @param {number} t - Progress value (0-1)
     * @returns {number} Eased value
     */
    easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    /**
     * Override update method to include tracking and POV mode
     */
    update() {
        if (this.controls && this.isEnabled) {
            if (this.povMode.enabled) {
                this.updatePOVMode();
            } else {
                this.updateTracking();
            }
            this.controls.update();
        }
    }

    /**
     * Set POV (Point of View) mode
     * @param {boolean} enabled - Whether POV mode should be enabled
     */
    setPOVMode(enabled) {
        if (enabled && !this.tracking.enabled) {
            console.warn('POV mode requires a planet to be tracked first');
            return false;
        }

        if (enabled && !this.povMode.enabled) {
            // Enable POV mode
            this.enablePOVMode();
        } else if (!enabled && this.povMode.enabled) {
            // Disable POV mode
            this.disablePOVMode();
        }

        return this.povMode.enabled;
    }

    /**
     * Enable POV mode - camera positioned at tracked planet with sun fixed in view
     */
    enablePOVMode() {
        if (!this.tracking.enabled || !this.tracking.targetObject) {
            console.warn('Cannot enable POV mode: no planet is being tracked');
            return;
        }

        if (!this.solarSystemObjects || !this.solarSystemObjects.sun) {
            console.warn('Cannot enable POV mode: solar system objects not set');
            return;
        }

        // Store original camera state
        this.povMode.originalCameraPosition.copy(this.camera.position);
        this.povMode.originalTarget.copy(this.controls.target);

        // Set references
        this.povMode.planetObject = this.tracking.targetObject;
        this.povMode.sunObject = this.solarSystemObjects.sun;
        this.povMode.enabled = true;

        // Reset POV-specific properties for fresh start
        this.povMode.initialPlanetToSun = null;
        this.povMode.fixedSunScreenPosition = null;
        this.povMode.cameraOffsetFromPlanet = null;

        console.log('POV mode enabled - heliocentric view from planet perspective');
    }

    /**
     * Disable POV mode - return to normal tracking
     */
    disablePOVMode() {
        if (!this.povMode.enabled) return;

        // Re-enable normal controls
        this.controls.enableRotate = this.settings.enableRotate;
        this.controls.enablePan = this.settings.enablePan;
        this.controls.enableZoom = this.settings.enableZoom;

        this.povMode.enabled = false;
        this.povMode.planetObject = null;
        this.povMode.sunObject = null;

        // Return to normal tracking mode
        if (this.tracking.enabled && this.tracking.targetObject) {
            this.updateTracking();
        }

        console.log('POV mode disabled - returned to normal tracking');
    }

    /**
     * Update POV mode camera positioning
     * In POV mode, the camera maintains a fixed position relative to the planet,
     * but the entire coordinate system rotates so the Sun appears stationary
     */
    updatePOVMode() {
        if (!this.povMode.enabled || !this.povMode.planetObject || !this.povMode.sunObject) {
            return;
        }

        const planetPosition = this.getTargetPosition(this.povMode.planetObject);
        const sunPosition = this.povMode.sunObject.position;

        if (planetPosition && sunPosition) {
            // Calculate the rotation needed to keep the sun in a fixed screen position
            const planetToSun = new THREE.Vector3().subVectors(sunPosition, planetPosition);

            // Store the initial planet-to-sun direction if not already stored
            if (!this.povMode.initialPlanetToSun) {
                this.povMode.initialPlanetToSun = planetToSun.clone().normalize();
                this.povMode.fixedSunScreenPosition = new THREE.Vector3(0, 0, -1); // Sun appears in front

                // Set initial camera position relative to planet
                const distanceToSun = planetToSun.length();
                const offsetDistance = Math.max(100, distanceToSun * 0.02);

                // Position camera behind the planet (opposite to sun direction)
                const cameraOffset = planetToSun
                    .clone()
                    .normalize()
                    .multiplyScalar(-offsetDistance);
                // Add some vertical offset for better perspective
                cameraOffset.add(new THREE.Vector3(0, offsetDistance * 0.2, 0));

                this.povMode.cameraOffsetFromPlanet = cameraOffset;
            }

            // Calculate current planet-to-sun direction
            const currentPlanetToSun = planetToSun.clone().normalize();

            // Calculate rotation needed to align current direction with initial direction
            const rotationAxis = new THREE.Vector3().crossVectors(
                currentPlanetToSun,
                this.povMode.initialPlanetToSun
            );
            const rotationAngle = Math.acos(
                Math.max(-1, Math.min(1, currentPlanetToSun.dot(this.povMode.initialPlanetToSun)))
            );

            // Create rotation matrix
            const rotationMatrix = new THREE.Matrix4();
            if (rotationAxis.length() > 0.001) {
                // Avoid division by zero
                rotationAxis.normalize();
                rotationMatrix.makeRotationAxis(rotationAxis, rotationAngle);
            }

            // Apply rotation to camera offset
            const rotatedCameraOffset = this.povMode.cameraOffsetFromPlanet.clone();
            rotatedCameraOffset.applyMatrix4(rotationMatrix);

            // Position camera relative to planet with rotation applied
            this.camera.position.copy(planetPosition).add(rotatedCameraOffset);

            // Calculate where the sun should appear (fixed position in screen space)
            const sunScreenPosition = planetPosition
                .clone()
                .add(this.povMode.initialPlanetToSun.clone().multiplyScalar(planetToSun.length()));

            // Point camera towards the fixed sun position
            this.controls.target.copy(sunScreenPosition);

            // Modo POV: bloquear TODOS los controles de usuario para una inmersión total.
            // La cámara queda fija a la perspectiva del planeta con el Sol estabilizado en pantalla;
            // permitir rotar/zoom rompía la ilusión. (Se restauran al salir de POV en disablePOVMode.)
            this.controls.enableRotate = false;
            this.controls.enablePan = false;
            this.controls.enableZoom = false;
        }
    }

    /**
     * Check if POV mode is enabled
     * @returns {boolean} True if POV mode is active
     */
    isPOVModeEnabled() {
        return this.povMode.enabled;
    }

    /**
     * Get POV mode state information
     * @returns {Object} POV mode state
     */
    getPOVModeState() {
        return {
            enabled: this.povMode.enabled,
            planet: this.povMode.planetObject,
            sun: this.povMode.sunObject,
        };
    }

    /**
     * Get tracking state information
     * @returns {Object} Tracking state
     */
    getTrackingState() {
        return {
            enabled: this.tracking.enabled,
            target: this.tracking.targetObject,
            distance: this.tracking.distance,
            isTransitioning: this.tracking.isTransitioning,
            smoothTransition: this.tracking.smoothTransition,
            povMode: this.povMode.enabled,
        };
    }
}
